import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageActionBarProps {
  /** Optional left-cluster content. Pair with `right` for the standard
   *  two-cluster layout (mirrors the PO detail bar). */
  left?: ReactNode;
  /** Optional right-cluster content. Pair with `left`. */
  right?: ReactNode;
  /** Free-form content used when `left`/`right` slots aren't supplied —
   *  lets a page hand the bar a pre-composed toolbar. */
  children?: ReactNode;
  className?: string;
  /** Aria-label for the inner nav. Defaults to "Page actions". */
  ariaLabel?: string;
  /**
   * Default true. Pass false on a full-height, non-scrolling page (the
   * Stocktakes inbox shell: `h-[calc(100dvh-…)] overflow-hidden`). On such a
   * page the wrapper's `overflow-hidden` makes IT the bar's sticky containing
   * scrollport, so the `--top-header-height` offset is measured against the
   * page instead of the app scroll surface — permanently pushing the bar down
   * by the header height and painting it over the first rows of the content
   * (BLU-711 staging feedback, 2026-08-05). A page that never scrolls needs no
   * stickiness: this renders the bar in normal flow instead.
   */
  sticky?: boolean;
}

/**
 * Generic sticky action bar that sits flush under TopHeader.
 *
 * Pattern lifted from `PoActionBar`: sentinel + IntersectionObserver toggles a
 * `data-stuck` attribute on the bar, which animates a subtle border + shadow
 * once the bar is pinned. Translucent backdrop-blur background matches the
 * rest of the page chrome.
 *
 * Two layout modes:
 *   - Slot mode: pass `left` and/or `right` for the two-cluster `justify-between` layout.
 *   - Children mode: pass `children` to render an arbitrary toolbar inside the bar.
 *
 * Z-index + offset (BLU-3xx Phase 47.01.2-B-fix CRIT-3):
 *   Z-index is fixed at `z-40` so the bar sits below TopHeader (z-50) and above
 *   BackToTopPill (z-30). Top offset reads `--top-header-height` (set by the
 *   page chrome) with a 3.5rem fallback PLUS `--shared-location-banner-height`
 *   (defaults to 0px — pages that mount `SharedLocationContextBanner`
 *   set this on a parent wrapper while the banner is visible so the
 *   action bar slides below it). The banner shares z-40 but appears
 *   FIRST in source order on every page, so it paints on top of any
 *   transient overlap.
 */
export function PageActionBar({
  left,
  right,
  children,
  className,
  ariaLabel = "Page actions",
  sticky = true,
}: PageActionBarProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    if (!sticky) return;
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setStuck(!entry.isIntersecting);
      },
      { rootMargin: "0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // BLU-411: publish the bar's rendered height as `--page-action-bar-height`
  // so sticky table headers (or any surface that must pin directly beneath the
  // bar) can offset by `calc(var(--top-header-height) + var(--page-action-bar-height))`.
  // Measured (not hardcoded) because the toolbar wraps to a taller row on
  // narrow viewports. Resets to 0px on unmount so pages without a bar are
  // unaffected. Same convention as `--shared-location-banner-height`.
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty("--page-action-bar-height", `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.setProperty("--page-action-bar-height", "0px");
    };
  }, []);

  const useSlots = left !== undefined || right !== undefined;

  return (
    <>
      {/* Sentinel — when this scrolls out of view, the bar becomes stuck. */}
      {sticky ? <div ref={sentinelRef} aria-hidden className="h-px -mt-px" /> : null}

      <div
        ref={barRef}
        className={sticky ? "sticky z-40" : "relative z-40"}
        style={
          sticky
            ? {
                // BLU-3xx Phase 47.01.2-B-fix CRIT-3: include
                // --shared-location-banner-height (0px when no banner is
                // mounted) so SharedLocationContextBanner can pin above the
                // action bar without overlap.
                top: "calc(var(--top-header-height, 3.5rem) + var(--shared-location-banner-height, 0px))",
              }
            : undefined
        }
        data-stuck={stuck || undefined}
      >
        <div
          className={cn(
            "relative -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8",
            "bg-background/85 supports-[backdrop-filter]:bg-background/65 backdrop-blur",
            "border-b border-transparent",
            "transition-[border-color,box-shadow] duration-200 motion-reduce:transition-none",
            "data-[stuck]:border-border/60 data-[stuck]:shadow-[0_4px_12px_-6px_rgb(0_0_0_/_0.08)]",
            className,
          )}
          data-stuck={stuck || undefined}
        >
          <nav
            aria-label={ariaLabel}
            className={cn(
              // Full-width, matching the page bodies below (w-full convention).
              // Was `mx-auto max-w-screen-2xl`, which centred the bar's content
              // independently of the full-width body — on wide monitors the
              // leftmost control floated right of the table under it (owner
              // feedback 2026-07-08: "align the tab to the left").
              "pt-3 pb-2.5",
              useSlots && "flex flex-wrap items-end justify-between gap-x-4 gap-y-2",
            )}
          >
            {useSlots ? (
              <>
                <div className="flex min-w-0 items-end gap-1.5 flex-wrap">{left}</div>
                {/* `ml-auto justify-end`: on a narrow screen the actions keep
                    their right edge when they drop under the left cluster or
                    wrap among themselves (justify-between alone would put a
                    lone wrapped line at the left). */}
                <div className="ml-auto flex flex-wrap items-end justify-end gap-1.5">{right}</div>
              </>
            ) : (
              children
            )}
          </nav>
        </div>
      </div>
    </>
  );
}

/**
 * Vertical divider for separating logical groups within a cluster.
 *
 * DESIGN §3.3 specifies two, and they are deliberately not the same rule: the
 * LEFT cluster's separates state/filter controls (`h-6`, `border/70` — taller
 * and slightly firmer, because the things either side of it are read as data),
 * the RIGHT cluster's separates action groups (`h-5`, `border/60` — quieter,
 * so it never competes with the buttons). Both exist here rather than as inline
 * spans so a third variant cannot appear by accident.
 *
 * `action` stays the default: it is what every existing consumer renders, and
 * its dimensions match PoActionBar's so the two bars look like siblings.
 *
 * The `filter` divider hides below `sm`: there the left cluster wraps, and a
 * divider left at the end of a line (after the Products view switch, or after
 * Stock Hold-Back's section pill) separates nothing. It is decoration only
 * (`aria-hidden`); the controls either side keep their own labels.
 */
export function PageActionBarDivider({
  variant = 'action',
}: {
  variant?: 'action' | 'filter';
}) {
  return (
    <span
      aria-hidden
      className={
        variant === 'filter'
          ? 'mx-1 h-6 w-px self-end bg-border/70 max-sm:hidden'
          : 'mx-1 h-5 w-px bg-border/60'
      }
    />
  );
}
